import { Injectable } from '@angular/core';
import { UserService } from '@modules/auth/services';
import { environment } from 'environments/environment';
import { Observable, Subject } from 'rxjs';
import { io, Socket } from 'socket.io-client';

/**
 * Wrapper genérico de Socket.io (cliente) para features en tiempo real.
 *
 * El backend (ver `api/src/sockets/socketServer.ts`) autentica en el handshake
 * espejando `authJwt`: valida el token, resuelve el usuario y une el socket a
 * `room_<tenantId>`. El cliente NO elige sala ni emite un join manual: basta con
 * enviar el token en `handshake.auth.token`.
 *
 * Transporte solo websocket (el server fuerza `transports: ['websocket']` para
 * evitar sticky sessions en multi-réplica); el cliente debe conectar igual.
 */
@Injectable({
    providedIn: 'root',
})
export class SocketService {
    private socket: Socket | null = null;

    // Handler del evento `reconnect` del Manager de socket.io (this.socket.io). Se guarda para poder
    // removerlo en `disconnect()`: socket.io-client reutiliza el mismo Manager por URL, así que sin
    // `off()` cada ciclo logout→login acumularía un listener más (N reconexiones → N resync HTTP).
    private _reconnectHandler: (() => void) | null = null;

    // Handler del evento `reconnect_attempt` del Manager: refresca el token JWT ANTES de cada reintento de
    // reconexión (ver `connect()`). Se guarda para removerlo en `disconnect()`, mismo motivo que `_reconnectHandler`.
    private _reconnectAttemptHandler: (() => void) | null = null;

    // Emite cada vez que el manager de Socket.io RECONECTA (no en la conexión
    // inicial). Lo consumen las features para re-sincronizar su estado por HTTP
    // y no perder eventos ocurridos durante la desconexión.
    private _reconnect$: Subject<void> = new Subject<void>();
    get reconnect$(): Observable<void> {
        return this._reconnect$.asObservable();
    }

    constructor(private userService: UserService) {}

    /**
     * Abre la conexión si no existe. Lee el token fresco del usuario autenticado;
     * si no hay token (sesión ausente o expirada) no intenta conectar.
     */
    connect(): void {
        if (this.socket) return;

        const token = this.userService.user$.value?.token;
        if (!token) {
            console.warn('[SocketService] connect() llamado sin token; socket no creado.');
            return;
        }

        // Host base sin el sufijo `/api` (Socket.io vive en la raíz del server).
        const url = environment.API_URL.replace(/\/api\/?$/, '');

        this.socket = io(url, {
            transports: ['websocket'],
            auth: { token },
            autoConnect: true,
        });

        // El manager reconecta solo; distinguimos la reconexión de la conexión
        // inicial para disparar el resync HTTP únicamente cuando corresponde.
        this._reconnectHandler = () => {
            this._reconnect$.next();
        };
        this.socket.io.on('reconnect', this._reconnectHandler);

        // Refresco de token en reconexión: socket.io-client reutiliza el `auth` capturado en la 1ª conexión en
        // TODOS los reintentos. Si el JWT expira durante una sesión larga, cada reconnect fallaría con
        // `connect_error` y —como `_reconnect$` solo emite en reconexiones EXITOSAS— el OnboardingService nunca
        // se enteraría ni re-sincronizaría por HTTP (el usuario perdería el tiempo real sin aviso). En cada
        // `reconnect_attempt` releemos el token vigente de `user$` y lo escribimos en `socket.auth` ANTES del
        // handshake del siguiente intento, de modo que la reconexión use el token fresco y, al reconectar,
        // `reconnect` emita y gatille el resync. No toca la conexión inicial (sigue usando el `auth` de arriba).
        this._reconnectAttemptHandler = () => {
            const freshToken = this.userService.user$.value?.token;
            if (freshToken && this.socket) {
                this.socket.auth = { token: freshToken };
            }
        };
        this.socket.io.on('reconnect_attempt', this._reconnectAttemptHandler);

        this.socket.on('connect_error', err => {
            // Útil si el token expira/rechaza en el handshake. No reventamos la app.
            console.warn('[SocketService] connect_error:', err?.message);
        });
    }

    /** Cierra la conexión y limpia la referencia. */
    disconnect(): void {
        if (!this.socket) return;
        // Remover el listener del Manager (this.socket.io) que NO limpia `socket.removeAllListeners()`.
        if (this._reconnectHandler) {
            this.socket.io.off('reconnect', this._reconnectHandler);
            this._reconnectHandler = null;
        }
        if (this._reconnectAttemptHandler) {
            this.socket.io.off('reconnect_attempt', this._reconnectAttemptHandler);
            this._reconnectAttemptHandler = null;
        }
        this.socket.removeAllListeners();
        this.socket.disconnect();
        this.socket = null;
    }

    /** Observable que emite cada payload recibido para `event`. */
    on<T>(event: string): Observable<T> {
        return new Observable<T>(subscriber => {
            if (!this.socket) {
                subscriber.complete();
                return;
            }
            const handler = (payload: T) => subscriber.next(payload);
            this.socket.on(event, handler);
            return () => {
                this.socket?.off(event, handler);
            };
        });
    }
}
